/**
 * Taking something off a bill.
 *
 * WHY THERE ARE TWO WAYS AND THEY LOOK DIFFERENT
 * ----------------------------------------------
 * An offer is a decision somebody else already made — head office set the
 * dates, the minimum, the cap. Applying one is following policy, so it needs
 * nobody's permission and the screen leads with it.
 *
 * A typed-in discount is this cashier deciding, right now, how much of the
 * bill to give away. That is the oldest way to take money out of a till, so
 * it sits second, it is capped, and past the cap it stops being this person's
 * decision at all.
 *
 * WHY THE OFFER IS CHECKED BEFORE THE BILL IS AGREED
 * --------------------------------------------------
 * "This offer needs a bill of Rs. 1,000 — you are Rs. 150 short" is worth
 * saying while the customer can still add a drink. After the total has been
 * agreed it is an argument.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { formatPKR } from '@foodrun/shared';

import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { useCart } from './cart.js';

export function DiscountPanel({ subtotal, orderType, approvalNeeded, onClose }) {
  const promotionCode = useCart((s) => s.promotionCode);
  const discount = useCart((s) => s.discount);
  const setPromotionCode = useCart((s) => s.setPromotionCode);
  const setDiscount = useCart((s) => s.setDiscount);

  const [code, setCode] = useState(promotionCode);
  const [value, setValue] = useState(discount ? String(discount.value) : '');
  const [kind, setKind] = useState(discount?.type ?? 'percentage');
  const [reason, setReason] = useState(discount?.reason ?? '');

  const check = useMutation({
    mutationFn: (typed) => api.post('/menu/promotions/check', { code: typed, subtotal, orderType }),
    onSuccess: (result, typed) => {
      if (result.applies) setPromotionCode(typed);
    },
  });

  const typed = Number(value);
  const validAmount = value === '' || (Number.isFinite(typed) && typed > 0);

  return (
    <div className="rounded-xl border border-rule bg-surface p-4">
      <h3 className="mb-3 font-medium text-ink">Take something off</h3>

      <div className="mb-4">
        <label className="mb-1.5 block text-xs text-ink-3">Offer code</label>
        <div className="flex gap-2">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="TUESDAY20"
            aria-label="Offer code"
            className="h-11 min-w-0 flex-1 rounded-lg border border-rule bg-surface-3 px-3 font-mono text-sm uppercase text-ink focus:border-brand-400 focus:outline-none"
          />
          <Button onClick={() => check.mutate(code)} disabled={!code.trim() || check.isPending}>
            {check.isPending ? 'Checking' : 'Apply'}
          </Button>
        </div>

        {/* The reason, not merely a refusal — a cashier has to say something
            to the customer that is not "the computer says no". */}
        {check.data && !check.data.applies && (
          <p className="mt-2 rounded-lg bg-warn-bg px-3 py-2 text-sm text-warn">
            {check.data.reason}
          </p>
        )}

        {check.data?.applies && (
          <p className="mt-2 rounded-lg bg-surface-3 px-3 py-2 text-sm text-ink-2">
            {check.data.promotion.name} —{' '}
            {check.data.freeDelivery ? 'delivery free' : `${formatPKR(check.data.discount)} off`}
          </p>
        )}

        {promotionCode && (
          <button
            type="button"
            onClick={() => {
              setPromotionCode('');
              setCode('');
              check.reset();
            }}
            className="mt-2 text-sm text-ink-2 underline hover:text-ink"
          >
            Remove {promotionCode}
          </button>
        )}
      </div>

      <div className="border-t border-rule pt-4">
        <label className="mb-1.5 block text-xs text-ink-3">Or a discount by hand</label>

        <div className="flex flex-wrap gap-2">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            aria-label="Discount kind"
            className="h-11 rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none"
          >
            <option value="percentage">Percent</option>
            <option value="fixed">Rupees</option>
          </select>

          <input
            type="number"
            min="0"
            step="0.01"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            aria-label="Discount amount"
            className="h-11 w-24 rounded-lg border border-rule bg-surface-3 px-3 text-right text-sm tabular-nums text-ink focus:border-brand-400 focus:outline-none"
          />

          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why"
            aria-label="Reason"
            className="h-11 min-w-0 flex-1 rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none"
          />
        </div>

        {!validAmount && (
          <p className="mt-2 text-sm text-warn">A discount has to be more than nothing.</p>
        )}

        {/*
          Shown the moment the figure goes over, while the cashier is still
          typing — not after the customer has agreed a total.
        */}
        {approvalNeeded && (
          <p className="mt-2 rounded-lg bg-warn-bg px-3 py-2 text-sm text-warn">
            {approvalNeeded.message}
          </p>
        )}
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button
          onClick={() => {
            setDiscount(
              value === '' ? null : { type: kind, value: typed, reason: reason.trim() || undefined }
            );
            onClose();
          }}
          disabled={!validAmount}
        >
          Done
        </Button>
      </div>
    </div>
  );
}
